import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {ReadCampaign, ReadPage} from '@tinct/core-api-models';
import {useExcludePage, useIncludePage, usePublishPage, useUnpublishPage} from '@tinct/core-api';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {
  ActionMenuItem,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@tinct/web-components';
import {ArrowUpFromLine, Ban, EllipsisVertical, Link2, Pen, ToggleLeft, ToggleRight} from 'lucide-react';
import {UnpublishPageModal} from './unpublish-page-modal';
import {getPageDisplayIdentity} from './get-page-display-identity';
import {getPageActionCapabilities} from './get-page-action-capabilities';

export type PageActionsMenuProps = {
  page: ReadPage;
  campaign?: ReadCampaign;
  triggerVariant?: 'ghost' | 'outline';
  triggerClassName?: string;
};

/**
 * The variant "More" dropdown (Edit / Exclude / Include / Unpublish / Publish /
 * Copy URL), shared between the page-variants list rows and the variant
 * dashboard header. Which items show, and which are greyed out with a reason,
 * comes from `getPageActionCapabilities`.
 */
export function PageActionsMenu({
  page,
  campaign,
  triggerVariant = 'ghost',
  triggerClassName = 'size-8',
}: PageActionsMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();

  const excludePage = useExcludePage({
    onSuccess: () => toast.success('Variant excluded from publishing'),
    onError: () => toast.error('Failed to exclude variant'),
  });
  const includePage = useIncludePage({
    onSuccess: () => toast.success('Variant included in publishing'),
    onError: () => toast.error('Failed to include variant'),
  });
  const publishPage = usePublishPage({
    onSuccess: () => toast.success('Variant published'),
    onError: () => toast.error('Failed to publish variant'),
  });
  const unpublishPage = useUnpublishPage({
    onSuccess: () => toast.success('Variant unpublished'),
    onError: () => toast.error('Failed to unpublish variant'),
  });

  const pageId = page.id ?? null;
  const campaignId = campaign?.id ?? null;
  const capabilities = getPageActionCapabilities(page, campaign);

  const handleEdit = () => {
    if (!campaignId || capabilities.edit.reason) return;
    const params = new URLSearchParams();
    if (capabilities.edit.mode === 'with-page') {
      if (pageId) params.set('pageId', pageId);
      const companyName = page.lead?.company?.name;
      if (companyName) params.set('q', companyName);
    }
    const query = params.toString();
    // Inside the funnel, Edit stays on the funnel's preview step (keeps the
    // setup chrome); everywhere else it opens the management studio.
    const base = location.pathname.includes('/funnel-campaigns/')
      ? `${workspaceUrl}/funnel-campaigns/${campaignId}/preview`
      : `${workspaceUrl}/campaigns/${campaignId}/studio`;
    navigate(`${base}${query ? `?${query}` : ''}`);
  };

  const handleExclude = () => {
    if (!pageId) return;
    excludePage.mutate({ pageId });
  };

  const handleInclude = () => {
    if (!pageId) return;
    includePage.mutate({ pageId });
  };

  // No confirmation: publishing is the recoverable direction (Unpublish is one
  // click away), unlike taking a live variant down.
  const handlePublish = () => {
    if (!pageId) return;
    publishPage.mutate({ pageId });
  };

  const handleCopyUrl = () => {
    if (!page.staticUrl) return;
    navigator.clipboard.writeText(page.staticUrl);
    toast.success('Copied link to clipboard');
  };

  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);

  const handleUnpublishConfirmed = () => {
    if (!pageId) return;
    unpublishPage.mutate(
      { pageId },
      { onSettled: () => setShowUnpublishConfirm(false) },
    );
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={triggerVariant}
          size="icon"
          className={triggerClassName}
          aria-label="Open variant actions menu"
        >
          <EllipsisVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <ActionMenuItem onSelect={handleEdit} blockedReason={capabilities.edit.reason}>
          <Pen />
          Edit
        </ActionMenuItem>

        {capabilities.exclude.visible && (
          <ActionMenuItem onSelect={handleExclude} blockedReason={capabilities.exclude.reason}>
            <ToggleLeft />
            Exclude from publishing
          </ActionMenuItem>
        )}

        {capabilities.include.visible && (
          <ActionMenuItem onSelect={handleInclude} blockedReason={capabilities.include.reason}>
            <ToggleRight />
            Include into publishing
          </ActionMenuItem>
        )}

        {capabilities.unpublish.visible && (
          <ActionMenuItem
            onSelect={() => setShowUnpublishConfirm(true)}
            blockedReason={capabilities.unpublish.reason}
          >
            <Ban />
            Unpublish
          </ActionMenuItem>
        )}

        {capabilities.publish.visible && (
          <ActionMenuItem onSelect={handlePublish} blockedReason={capabilities.publish.reason}>
            <ArrowUpFromLine />
            Publish
          </ActionMenuItem>
        )}

        <ActionMenuItem onSelect={handleCopyUrl} blockedReason={capabilities.copyUrl.reason}>
          <Link2 />
          Copy URL
        </ActionMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <UnpublishPageModal
      open={showUnpublishConfirm}
      onOpenChange={setShowUnpublishConfirm}
      pageName={getPageDisplayIdentity(page).label}
      isPending={unpublishPage.isPending}
      onConfirm={handleUnpublishConfirmed}
    />
    </>
  );
}
